
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Currency, SUPPORTED_CURRENCIES, formatPrice } from '../lib/currencies';

interface CurrencyContextType {
  currentCurrency: Currency;
  setCurrency: (code: string) => void;
  format: (amount: number) => string;
  convert: (amount: number) => number;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

export const CurrencyProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentCurrency, setCurrentCurrency] = useState<Currency>(() => {
    const saved = localStorage.getItem('lynqar_currency');
    return SUPPORTED_CURRENCIES.find(c => c.code === saved) || SUPPORTED_CURRENCIES[0];
  });

  const setCurrency = (code: string) => {
    const found = SUPPORTED_CURRENCIES.find(c => c.code === code);
    if (found) {
      setCurrentCurrency(found);
      localStorage.setItem('lynqar_currency', code);
    }
  };

  const format = (amount: number) => formatPrice(amount, currentCurrency);
  const convert = (amount: number) => amount * currentCurrency.rate;

  return (
    <CurrencyContext.Provider value={{ currentCurrency, setCurrency, format, convert }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) throw new Error('useCurrency must be used within a CurrencyProvider');
  return context;
};
